import { z } from 'zod';
import { FileSchema } from './file.types';
import { nullableObject } from './global.types';

export const crewSchema = z.object({
    id: z.string(),
    crew_name: z.string(),
    color: z.string(),
    crew_abbreviation: z.string(),
    start_of_week: z.string()
});

export type Crew = z.infer<typeof crewSchema>;

export const workerSchema = z.object({
    ID: z.string(),
    display_name: z.string(),
    user_email: z.string()
});

export type Worker = z.infer<typeof workerSchema>;

export const clientSchema = z.object({
    id: z.number(),
    title: z.string(),
    postalCode: z.string().nullable(),
    postalAddress: z.string().nullable(),
    phoneNumber: z.string().nullable()
});

export type Client = z.infer<typeof clientSchema>;

export const projectSchema = z.object({
    id: z.number(),
    title: z.string(),
    client: z.number(),
    address: z.string()
});

export type Project = z.infer<typeof projectSchema>;

// export const clientFileSchema = z.object({
//     src: z.string(),
//     name: z.string(),
//     id: z.number()
// });

// export type ClientFile = z.infer<typeof clientFileSchema>;

// export const factoryFileSchema = z.object({
//     src: z.string(),
//     name: z.string()
// });

// export type FactoryFile = z.infer<typeof factoryFileSchema>;

// export const designerFileSchema = z.object({
//     src: z.string(),
//     name: z.string()
// });

// export type DesignerFile = z.infer<typeof designerFileSchema>;

const mountingOrderVariant = ['survey', 'fitting'] as const;
export const mountingOrderVariantSchema = z.enum(mountingOrderVariant);
export type MountingOrderVariant = (typeof mountingOrderVariant)[number];

export const eventSchema = z.object({
    Id: z.string(),
    Subject: z.string(),
    StartTime: z.date(),
    EndTime: z.date(),
    project: z.number(),
    client: z.string(),
    clientPostalPlace: z.string(),
    clientPostalAddress: z.string(),
    clientPhoneNumber: z.string(),
    author: z.string(),
    comment: z.string(),
    workers: z.array(workerSchema),
    address: z.string().optional(),
    postalCode: z.string().optional(),
    postalPlace: z.string().optional(),
    phoneNumber: z.string().optional(),
    clientFiles: z.array(FileSchema).nullable(),
    factoryFiles: z.array(FileSchema).nullable(),
    designerFiles: z.array(FileSchema).nullable(),
    documentationFiles: z.array(FileSchema).nullable(),
    region: z.string(),
    color: z.string(),
    local_id: z.string().optional(),
    variant: mountingOrderVariantSchema,
    feedback: z.string().nullable()
});

export type Event = z.infer<typeof eventSchema>;

export const minimizedEventSchema = z.object({
    // Id: z.string(),
    Subject: z.string(),
    StartTime: z.date(),
    EndTime: z.date(),
    color: z.string()
});

export type MinimizedEvent = z.infer<typeof minimizedEventSchema>;

export const rootSchema = z.object({
    workers: z.array(workerSchema),
    clients: z.array(clientSchema),
    projects: z.array(projectSchema),
    events: z.array(eventSchema)
});

export type Root = z.infer<typeof rootSchema>;

// export const fittingFeedbackDaySchema = z.object({
//     id: z.number(),
//     user_id: z.number(),
//     mounting_order_id: z.number(),
//     date: z.number(),
//     feedback: z.string()
// });

// export type FittingFeedbackDay = z.infer<typeof fittingFeedbackDaySchema>;

export const mountingOrderMinimizedJobSchema = z.object({
    id: z.number(),
    orderId: z.number(),
    region: z.number(),
    address: z.string(),
    feedback: z.string(),
    date: z.number(),
    startTime: z.string(),
    endTime: z.string()
});

export type MountingOrderMinimizedJob = z.infer<typeof mountingOrderMinimizedJobSchema>;

export const baseMountingOrderSchema = z.object({
    id: z.number(),
    orderId: z.number(),
    userId: z.number(),
    variant: mountingOrderVariantSchema,
    extra: z.string().nullable(),
    clientConfirmed: z.boolean(),
    title: z.string(),
    shippingDate: z.number().nullable(),
    creationDate: z.string(),
    mountingJobs: z.array(nullableObject(mountingOrderMinimizedJobSchema)),
    extraWorkOrChangesRequired: z.boolean(),
    extraWorkOrChangesDescription: z.string().nullable()
});

export const surveyMountingOrderSchema = z.object({
    ...baseMountingOrderSchema.shape,
    variant: z.literal('survey'),
    surveyCost: z.number().nullable(),
    surveyStartDate: z.number().nullable(),
    surveyEndDate: z.number().nullable(),
    surveyUploadDates: z.array(z.number()),
    extraSurveyRequired: z.boolean(),
    formFileId: z.number().nullable(),
    formFileSigned: z.boolean(),
    formFile: z.any().nullable()
});

export type SurveyMountingOrder = z.infer<typeof baseMountingOrderSchema>;

export const fittingMountingOrderSchema = z.object({
    ...baseMountingOrderSchema.shape,
    variant: z.literal('fitting'),
    fittingCost: z.number().nullable(),
    fittingStartDate: z.number().nullable(),
    fittingEndDate: z.number().nullable()
});

export type FittingMountingOrder = z.infer<typeof fittingMountingOrderSchema>;

export const mountingOrderSchema = z.discriminatedUnion('variant', [surveyMountingOrderSchema, fittingMountingOrderSchema]);
export type MountingOrder = z.infer<typeof mountingOrderSchema>;
